// frontend/src/components/shared/BackLink.tsx
import { Button, type SystemStyleObject } from '@chakra-ui/react'
import { FiArrowLeft } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import type { BackTarget } from '../../lib/returnNav'

/**
 * LW-9 tap target. `size="sm"` already resolves to 44px on a COARSE pointer via the button
 * recipe's `_touch` (theme/recipes/button.recipe.ts:30), and to 40px comfortable / 32px
 * compact on a FINE pointer. The gap the house ≥44px rule cares about is narrow viewport +
 * fine pointer (a small desktop window, DevTools at 390, the Playwright visual projects),
 * so raise the height below the md breakpoint and leave md+ exactly as it is today.
 *
 * The nested `_compact` repeat is REQUIRED: `[data-density=compact] &` out-specifies a bare
 * media rule and would otherwise win at 32px inside the same viewport range.
 */
// Shared CSS constant co-located with the sole component that uses it (its LW-9 test imports
// it too); the react-refresh disable is a dev-only fast-refresh nicety, matching the repo's
// established component-plus-constant export pattern.
// eslint-disable-next-line react-refresh/only-export-components
export const BACK_LINK_TOUCH_CSS: SystemStyleObject = {
  mdDown: {
    h: '2.75rem',
    minH: '2.75rem',
    _compact: { h: '2.75rem', minH: '2.75rem' },
  },
}

/**
 * Consistent top-left "back" affordance (LW-9). Navigates client-side (never window.location)
 * to `to`. Placement/spacing is owned by PageContainer; render it there, not ad hoc.
 */
export function BackLink({ to, label }: BackTarget) {
  const navigate = useNavigate()
  return (
    <Button
      variant="ghost"
      colorPalette="gray"
      size="sm"
      ml={-2}
      css={BACK_LINK_TOUCH_CSS}
      onClick={() => navigate(to)}
    >
      <FiArrowLeft />
      {label}
    </Button>
  )
}
